/**
 * Presets curados de formato para la UI admin del ColumnConfigModal.
 * Cada preset es una combinación pre-configurada de los campos de ColumnFormat.
 *
 * Spec: docs/architecture/COLUMN_CONFIG_PROPOSAL.md → Presets curados
 */
import type { ColumnFormat, FormatType } from './columnFormatters';

export interface ColumnFormatPreset {
  id: string;
  label: string;            // i18n key suffix (admin.columnConfig.formats.<id>)
  format: ColumnFormat;
  isCustom?: boolean;       // si true, el admin puede editar todos los campos en modal
}

export const FORMAT_PRESETS: ColumnFormatPreset[] = [
  {
    id: 'text',
    label: 'Texto',
    format: { format: 'text' },
  },
  {
    id: 'number_int',
    label: 'Número (entero)',
    format: { format: 'number', decimals: 0, thousandsSeparator: true },
  },
  {
    id: 'number_1d',
    label: 'Número (1 decimal)',
    format: { format: 'number', decimals: 1, thousandsSeparator: true },
  },
  {
    id: 'number_2d',
    label: 'Número (2 decimales)',
    format: { format: 'number', decimals: 2, thousandsSeparator: true },
  },
  {
    id: 'percentage_int',
    label: 'Porcentaje (entero)',
    format: { format: 'percentage', decimals: 0, thousandsSeparator: false },
  },
  {
    id: 'percentage_1d',
    label: 'Porcentaje (1 decimal)',
    format: { format: 'percentage', decimals: 1, thousandsSeparator: false },
  },
  {
    id: 'currency_ars',
    label: 'Moneda ARS ($1.234,56)',
    format: { format: 'currency', currencySymbol: '$', decimals: 2, thousandsSeparator: true },
  },
  {
    id: 'currency_usd',
    label: 'Moneda USD ($1,234.56)',
    format: { format: 'currency', currencySymbol: 'USD', decimals: 2, thousandsSeparator: true },
  },
  {
    id: 'date',
    label: 'Fecha',
    format: { format: 'date' },
  },
  {
    id: 'boolean',
    label: 'Booleano (Sí/No)',
    format: { format: 'boolean' },
  },
  {
    id: 'merged-toma-catalog',
    label: 'Merge Toma + Catalog (silencioso, tooltip)',
    format: { format: 'text' },
    // Legacy preset: el merge ya NO se activa por preset. Ahora es una propiedad
    // de la columna existente (`columnConfig.{core|native}[col].mergeWithCatalog`).
    // Se mantiene este preset para no romper docs viejas y se OCULTA del dropdown.
  },
  {
    id: 'custom',
    label: 'Custom...',
    format: { format: 'text' },
    isCustom: true,
  },
];

/**
 * Encuentra el preset más cercano a un ColumnFormat dado (para mostrar el
 * dropdown con la opción correcta seleccionada al cargar).
 * Si no matchea ninguno, devuelve 'custom'.
 */
export function findClosestPreset(fmt: ColumnFormat | undefined): string {
  if (!fmt || !fmt.format) return 'text';
  for (const p of FORMAT_PRESETS) {
    if (p.isCustom) continue;
    if (
      p.format.format === fmt.format &&
      (p.format.decimals ?? 0) === (fmt.decimals ?? 0) &&
      (p.format.thousandsSeparator ?? true) === (fmt.thousandsSeparator ?? true) &&
      (p.format.currencySymbol ?? '$') === (fmt.currencySymbol ?? '$')
    ) {
      return p.id;
    }
  }
  return 'custom';
}

/**
 * Devuelve el ColumnFormat de un preset por id. Útil al cambiar selección
 * en el dropdown del admin.
 */
export function presetToFormat(presetId: string): ColumnFormat {
  const p = FORMAT_PRESETS.find(p => p.id === presetId);
  if (!p) return { format: 'text' };
  return { ...p.format };
}

/**
 * Lista de tipos de format válidos. Espejo de VALID_FORMATS en backend.
 */
export const VALID_FORMAT_TYPES: FormatType[] = [
  'text',
  'number',
  'percentage',
  'currency',
  'date',
  'boolean',
];
